import React, { FC, useEffect } from 'react';

import { Navigate, useLocation } from 'react-router-dom';

import FullscreenPreloader from '~/components/atoms/FullscreenPreloader';
import { useAppSelector } from '~/store/hooks/useAppSelector';

import { resolvePolyllmLocale } from './localePath';
import { POLYLLM_DEFAULT_LOCALE } from './locales';

/**
 * Bare `/` entrypoint. Resolves the locale (saved platform language → browser
 * language → en) and sends the user to the chat index.
 *
 * - `en` (default): a client-side redirect to `/chat/index` (router `basename`
 *   is already the root).
 * - non-default locale: a full navigation to `/{locale}/chat/index`, because
 *   the router `basename` is fixed at mount and only a reload can switch it.
 */
const LocaleRootRedirect: FC = () => {
  const { search, hash } = useLocation();
  const savedLang = useAppSelector((state) => state.profile.platform_lang);

  const locale = resolvePolyllmLocale(savedLang, navigator.language);
  const isDefault = locale === POLYLLM_DEFAULT_LOCALE;

  useEffect(() => {
    if (!isDefault) {
      window.location.replace(`/${locale}/chat/index${search}${hash}`);
    }
  }, [isDefault, locale, search, hash]);

  if (isDefault) {
    return <Navigate replace to={{ pathname: '/chat/index', search, hash }} />;
  }

  return <FullscreenPreloader />;
};

export default LocaleRootRedirect;
